/**
 * LRU (Least Recently Used) Cache
 * 메모리 효율적인 캐싱을 위한 LRU 캐시 구현
 */

export class LRUCache<K, V> {
  private cache: Map<K, V>
  private readonly maxSize: number
  private readonly maxMemory: number // bytes
  private currentMemory: number = 0
  private hitCount: number = 0
  private missCount: number = 0
  private evictionCount: number = 0
  
  constructor(options: {
    maxSize?: number
    maxMemory?: number // MB
    onEvict?: (key: K, value: V) => void
  }) {
    this.maxSize = options.maxSize || 100
    this.maxMemory = (options.maxMemory || 50) * 1024 * 1024 // Convert MB to bytes
    this.cache = new Map()
    this.onEvict = options.onEvict
  }
  
  private onEvict?: (key: K, value: V) => void
  
  /**
   * Get value from cache
   */
  get(key: K): V | undefined {
    const value = this.cache.get(key)
    
    if (value !== undefined) {
      // Move to end (most recently used)
      this.cache.delete(key)
      this.cache.set(key, value)
      this.hitCount++
      return value
    }
    
    this.missCount++
    return undefined
  }
  
  /**
   * Set value in cache
   */
  set(key: K, value: V, sizeInBytes?: number): void {
    // Remove old value if exists
    if (this.cache.has(key)) {
      const oldValue = this.cache.get(key)!
      this.cache.delete(key)
      if (sizeInBytes) {
        this.currentMemory -= this.estimateSize(oldValue)
      }
    }
    
    // Calculate size
    const itemSize = sizeInBytes || this.estimateSize(value)
    
    // Evict items if necessary
    while (
      (this.cache.size >= this.maxSize || this.currentMemory + itemSize > this.maxMemory) &&
      this.cache.size > 0
    ) {
      const firstKey = this.cache.keys().next().value
      if (firstKey !== undefined) {
        this.evict(firstKey)
      }
    }
    
    // Add new item
    this.cache.set(key, value)
    this.currentMemory += itemSize
  }
  
  /**
   * Check if key exists
   */
  has(key: K): boolean {
    return this.cache.has(key)
  }
  
  /**
   * Delete item from cache
   */
  delete(key: K): boolean {
    const value = this.cache.get(key)
    if (value !== undefined) {
      this.cache.delete(key)
      this.currentMemory -= this.estimateSize(value)
      return true
    }
    return false
  }
  
  /**
   * Clear all cache
   */
  clear(): void {
    this.cache.clear()
    this.currentMemory = 0
    this.hitCount = 0
    this.missCount = 0
    this.evictionCount = 0
  }
  
  /**
   * Get cache size
   */
  get size(): number {
    return this.cache.size
  }
  
  /**
   * Get memory usage
   */
  get memoryUsage(): number {
    return this.currentMemory
  }
  
  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    const hitRate = this.hitCount + this.missCount > 0
      ? this.hitCount / (this.hitCount + this.missCount)
      : 0
    
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      memoryUsage: this.currentMemory,
      maxMemory: this.maxMemory,
      hitCount: this.hitCount,
      missCount: this.missCount,
      hitRate,
      evictionCount: this.evictionCount
    }
  }
  
  /**
   * Evict least recently used item
   */
  private evict(key: K): void {
    const value = this.cache.get(key)
    if (value !== undefined) {
      this.cache.delete(key)
      this.currentMemory -= this.estimateSize(value)
      this.evictionCount++
      
      if (this.onEvict) {
        this.onEvict(key, value)
      }
    }
  }
  
  /**
   * Estimate object size in bytes
   */
  private estimateSize(obj: any): number {
    if (obj === null || obj === undefined) return 0
    
    switch (typeof obj) {
      case 'string':
        return obj.length * 2 // UTF-16
      case 'number':
        return 8
      case 'boolean':
        return 4
      case 'object':
        if (obj instanceof ArrayBuffer) {
          return obj.byteLength
        }
        // Rough estimation for objects
        return JSON.stringify(obj).length * 2
      default:
        return 0
    }
  }
  
  /**
   * Get all keys (in LRU order)
   */
  keys(): K[] {
    return Array.from(this.cache.keys())
  }
  
  /**
   * Get all values (in LRU order)
   */
  values(): V[] {
    return Array.from(this.cache.values())
  }
  
  /**
   * Get all entries (in LRU order)
   */
  entries(): [K, V][] {
    return Array.from(this.cache.entries())
  }
}

export interface CacheStats {
  size: number
  maxSize: number
  memoryUsage: number
  maxMemory: number
  hitCount: number
  missCount: number
  hitRate: number
  evictionCount: number
}

/**
 * Document Cache with TTL support
 */
export class DocumentLRUCache extends LRUCache<string, CachedDocument> {
  private ttl: number // milliseconds
  
  constructor(options: {
    maxSize?: number
    maxMemory?: number
    ttl?: number // seconds
    onEvict?: (key: string, value: CachedDocument) => void
  }) {
    super(options)
    this.ttl = (options.ttl || 300) * 1000 // Convert to ms (default 5 minutes)
  }
  
  /**
   * Get with TTL check
   */
  get(key: string): CachedDocument | undefined {
    const doc = super.get(key)
    
    if (doc && this.isExpired(doc)) {
      this.delete(key)
      return undefined
    }
    
    return doc
  }
  
  /**
   * Set with timestamp
   */
  set(key: string, value: CachedDocument): void {
    value.cachedAt = Date.now()
    super.set(key, value, value.sizeInBytes)
  }
  
  /**
   * Check if document is expired
   */
  private isExpired(doc: CachedDocument): boolean {
    return Date.now() - doc.cachedAt > this.ttl
  }
  
  /**
   * Clean expired items
   */
  cleanExpired(): number {
    let cleaned = 0
    const now = Date.now()
    
    for (const [key, doc] of this.entries()) {
      if (now - doc.cachedAt > this.ttl) {
        this.delete(key)
        cleaned++
      }
    }
    
    return cleaned
  }
}

export interface CachedDocument {
  id: string
  path: string
  title: string
  content: string
  contentHash: string
  tokens: string[]
  metadata: any
  cachedAt: number
  sizeInBytes?: number
}